import React, { useState } from 'react';
import styled from 'styled-components';
import { Formik, yupToFormErrors } from 'formik';
import Form from 'components/ui/Form';
import * as Yup from 'yup';
import FlatButton from 'material-ui/FlatButton';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import CheckboxNormal from 'components/ui/CheckboxNormal';

import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import withMobileDialog from '@material-ui/core/withMobileDialog';

const StyledHeader = styled.div`
  background-color: #f7f7f7;
  margin: 20px -15px;
  padding-top: 8px;
  text-align: center;
  color: ${colors.FADE_GRAY};
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HidableItem = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
`;

const AssistanceRequest = ({ requiresAssistance, assistanceRequest, submitAssistanceRequest}) => {
  const [loading, setLoading] = useState(false);
  const [charLength, setCharLength] = useState((assistanceRequest && assistanceRequest.length) || 0);

  const handleSubmit = (data) => {
    setLoading(true)
    submitAssistanceRequest(data)
      .then((resp) => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  };

  const handleCheckboxChanged = (setFieldValue, value) => {
    setFieldValue('requires_assistance', value);
  }

  const handleTextChanged = (setFieldValue, value) => {
    if (value.length <= 250) {
      setFieldValue('assistance_request', value);
      setCharLength(value.length);
    }
  }

  const validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    let validationSchema = {};

    if (values.requires_assistance) {
      validationSchema['assistance_request'] = Yup.string().required(requiredErrorVerbiage);
    }

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  return (
    <React.Fragment>
      <StyledHeader className="row">
        <h5> {getTranslatedString('portal.assistanceRequest.title')}</h5>
      </StyledHeader>
      <p>{getTranslatedString('portal.assistanceRequest.description')}</p>
      <p>* {getTranslatedString('portal.assistanceRequest.info')}</p>

      <Formik
        initialValues={{
          requires_assistance: requiresAssistance,
          assistance_request: assistanceRequest || ""
        }}
        validate={validateForm}
        onSubmit={handleSubmit}
        validateOnChange={false}
        enableReinitialize
      >
        {({ values, errors, setFieldValue, submitForm, dirty }) => (
          <StyledForm>
            <CheckboxNormal
              name="requires_assistance"
              value={values.requires_assistance}
              checked={values.requires_assistance}
              onChange={(e) =>  handleCheckboxChanged(setFieldValue, e.target.checked)}
              style={{ marginTop: '15px' }}
            >
              {getTranslatedString('portal.assistanceRequest.checkboxText')}
            </CheckboxNormal>
            <HidableItem visible={values.requires_assistance}>
              <Spacer mb={1}>
                <Row gutter={16}>
                  <Col span={24}>
                    <div>{getTranslatedString('portal.assistanceRequest.textAreaLabel')} *</div>
                    <Form.Item
                      validateStatus={errors.assistance_request && 'error'}
                      help={errors.assistance_request}
                    >
                      <FullWidthTextArea
                        name="assistance_request"
                        value={values.assistance_request}
                        onChange={(e) => handleTextChanged(setFieldValue, e.target.value)}
                        placeholder=""
                        size="large"
                        rows="5"
                      />
                    </Form.Item>
                    {errors.assistance_request}
                    {getTranslatedString('portal.assistanceRequest.charsTyped')} {': '} {charLength + "/250"}
                  </Col>
                </Row>
              </Spacer>
            </HidableItem>
            <FlatButton
              onClick={() => submitForm()}
              id="submit-button"
              style={{
                color: !dirty || loading ? 'grey' : 'white',
                backgroundColor: !dirty || loading ? colors.LIGHT_GRAY : AccentColor(),
              }}
              disabled={!dirty || loading}
            >
              {getTranslatedString('save')}
            </FlatButton>
          </StyledForm>
        )}
      </Formik>
    </React.Fragment>
  );
};

export default withMobileDialog()(AssistanceRequest);
